/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Product } from '../services/api';
import { Shop, ShopProduct } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import CustomerHeader from '../components/CustomerHeader';
import ShopSelectionModal from '../components/ShopSelectionModal';
import { useTranslations } from '../hooks/useTranslations';
import { useCategoryTranslation } from '../utils/categoryTranslations';
import { useCartCount } from '../hooks/useCartCount';
import { useLanguage } from '../hooks/useLanguage';
import './BrowseAllPage.css';
import './CustomerHomePage.css';
import '../App.css';

/**
 * Browse All Page - Product-Focused Discovery
 * 
 * Purpose: Browse and filter PRODUCTS by category across all shops
 * 
 * Key Features:
 * - Category-based product browsing
 * - Product search and filters (price, sort)
 * - Contextual shop selection (only when adding to cart)
 * 
 * Note: This page is PRODUCT-focused, not shop-focused.
 * For shop discovery, users should use the "Explore Shops" page.
 * Shops only appear contextually via ShopSelectionModal when adding products to cart.
 */
const BrowseAllPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const t = useTranslations();
  const translateCategoryName = useCategoryTranslation();
  
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [apiCategories, setApiCategories] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'price-low' | 'price-high'>('name');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number }>({ min: 0, max: 10000 });
  
  // Shop selection modal - ONLY used contextually when adding products to cart
  // This is NOT for shop discovery - shops are not displayed as primary content
  const [showShopModal, setShowShopModal] = useState(false);
  const [selectedProductForCart, setSelectedProductForCart] = useState<Product | null>(null);
  
  // Wishlist state
  const [wishlist, setWishlist] = useState<any[]>([]);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(true);

  // Load wishlist from API
  useEffect(() => {
    const loadWishlist = async () => {
      if (!isAuthenticated || !user?.id) {
        // Fallback to localStorage for guests
        try {
          const saved = localStorage.getItem('wishlist');
          setWishlist(saved ? JSON.parse(saved) : []);
        } catch {
          setWishlist([]);
        }
        setIsLoadingWishlist(false);
        return;
      }

      try {
        setIsLoadingWishlist(true);
        const wishlistData = await apiService.getWishlist(user.id);
        setWishlist(wishlistData || []);
      } catch (error) {
        console.error('Error loading wishlist:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('wishlist');
          setWishlist(saved ? JSON.parse(saved) : []);
        } catch {
          setWishlist([]);
        }
      } finally {
        setIsLoadingWishlist(false);
      }
    };

    loadWishlist();
  }, [isAuthenticated, user?.id]);
  
  // Use shared hooks for cart count and language
  const cartCount = useCartCount(500); // Poll every 500ms
  const { language, setLanguage } = useLanguage();

  // Get category filter from navigation state
  const categoryFilter = useMemo(() => {
    const state = location.state as any;
    return {
      categoryId: state?.categoryId,
      categoryName: state?.categoryName,
      filterType: state?.filterType,
    };
  }, [location.state]);

  // Load level 3 categories only, and filter out categories with no products
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        // Fetch only level 3 categories (lvlcategory=3)
        const categories = await apiService.getCategoriesForSearch(3);
        console.log('📦 Loaded level 3 categories from API:', categories.length);
        
        if (categories && categories.length > 0) {
          // Filter categories to only include those that have products
          const categoriesWithProducts = await filterCategoriesWithProducts(categories);
          console.log(`✅ Filtered to ${categoriesWithProducts.length} categories with products (out of ${categories.length})`);
          setApiCategories(categoriesWithProducts);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Helper function to filter categories that have products
  const filterCategoriesWithProducts = async (categories: any[]): Promise<any[]> => {
    const categoriesWithProducts: any[] = [];
    
    // Load all products once to check which categories have products
    let allProducts: any[] = [];
    try {
      // Set high limit to get all products for filtering
      allProducts = await apiService.getProducts({ limit: 10000 });
      console.log(`📦 Loaded ${allProducts.length} total products to check category availability`);
    } catch (error) {
      console.error('Error loading products for category filtering:', error);
      // If we can't load products, return all categories (better to show all than none)
      return categories;
    }
    
    // Check each category to see if it has products
    for (const category of categories) {
      const categoryId = category.id;
      const categoryName = category.name || category.display_name;
      
      // Check if any product has this category_id
      const hasProducts = allProducts.some((product: any) => {
        const productCategoryId = product.category_id || 
                                 product.categoryId || 
                                 product.subcategory_id ||
                                 product.subcategoryId;
        
        // Match by category_id
        if (productCategoryId && productCategoryId.toString() === categoryId.toString()) {
          return true;
        }
        
        // Also check by category name as fallback
        const productCategoryName = (product.category || product.category_name || '').toLowerCase();
        const catName = (categoryName || '').toLowerCase();
        if (productCategoryName && catName && 
            (productCategoryName === catName || 
             productCategoryName.includes(catName) || 
             catName.includes(productCategoryName))) {
          return true;
        }
        
        return false;
      });
      
      if (hasProducts) {
        categoriesWithProducts.push(category);
        console.log(`✅ Category "${categoryName}" (ID: ${categoryId}) has products`);
      } else {
        console.log(`❌ Category "${categoryName}" (ID: ${categoryId}) has no products - filtering out`);
      }
    }
    
    return categoriesWithProducts;
  };

  // Set initial category from navigation state
  useEffect(() => {
    if (categoryFilter.categoryId) {
      setSelectedCategory(categoryFilter.categoryId);
      handleCategorySelect(categoryFilter.categoryId, categoryFilter.categoryName);
    }
  }, [categoryFilter]);


  // Helper function to get category icon
  const getCategoryIcon = (categoryName: string): string => {
    const iconMap: { [key: string]: string } = {
      'bakeries': '🥖', 'bakery': '🥖', 'bread': '🥖', 'pastries': '🥖',
      'sweet': '🍬', 'sweets': '🍬', 'candy': '🍬',
      'home food': '🏠', 'home-food': '🏠', 'homefood': '🏠',
      'pickles': '🥒', 'pickle': '🥒',
      'dairy': '🥛', 'milk': '🥛', 'cheese': '🥛',
      'fruits': '🍎', 'vegetables': '🍎', 'fruits & veg': '🍎', 'fruits-veg': '🍎', 'produce': '🍎',
      'flowers': '🌸', 'flower': '🌸',
      'meat': '🥩', 'chicken': '🥩', 'fish': '🥩',
      'kirana': '🏪', 'grocery': '🏪', 'groceries': '🏪',
      'organic': '🌿', 'organic products': '🌿',
      'rice': '🌾', 'grains': '🌾', 'rice & grains': '🌾', 'rice-grains': '🌾',
      'dry fruits': '🥜', 'dry-fruits': '🥜', 'nuts': '🥜',
      'pharmacy': '💊', 'medications': '💊', 'medicine': '💊',
      'ayurvedic': '🌿', 'ayurveda': '🌿',
      'dental': '🦷', 'dental care': '🦷', 'dental-care': '🦷',
      'wellness': '🧘', 'health': '🧘'
    };
    
    const lowerName = categoryName.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerName.includes(key)) {
        return icon;
      }
    }
    return '📦';
  };

  const getCategoryImagePath = (categoryName: string): string | null => {
    if (!categoryName) return null;
    
    const name = categoryName.toLowerCase().trim();
    const imageMap: { [key: string]: string } = {
      'fruits': '/fruits & vegetables.jpg',
      'vegetables': '/fruits & vegetables.jpg',
      'fruits & vegetables': '/fruits & vegetables.jpg',
      'fruits-vegetables': '/fruits & vegetables.jpg',
      'fruits and vegetables': '/fruits & vegetables.jpg',
      'fruits & veg': '/fruits & vegetables.jpg',
      'fruits-veg': '/fruits & vegetables.jpg',
      'produce': '/fruits & vegetables.jpg',
      'home food': '/home cooking image.jpg',
      'home-food': '/home cooking image.jpg',
      'home cooking': '/home cooking image.jpg',
      'homefood': '/home cooking image.jpg',
      'cooking': '/home cooking image.jpg',
      'pickles': '/pickle-shop-image.png',
      'pickle': '/pickle-shop-image.png',
    };
    
    if (imageMap[name]) {
      return imageMap[name];
    }
    
    for (const [key, imagePath] of Object.entries(imageMap)) {
      if (name.includes(key) || key.includes(name)) {
        return imagePath;
      }
    }
    
    return null;
  };

  // Get all level 3 categories in a flat list
  const allLevel3Categories = useMemo(() => {
    if (apiCategories.length === 0) return [];
    
    return apiCategories.map(cat => ({
      id: cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
      name: translateCategoryName(cat.display_name || cat.name || 'Unknown'),
      icon: cat.icon || getCategoryIcon(cat.name || ''),
      image: cat.image_url ? getValidImageUrl(cat.image_url) : (cat.image ? getValidImageUrl(cat.image) : getCategoryImagePath(cat.name || '')),
      categoryId: cat.id,
      categoryName: cat.name
    }));
  }, [apiCategories, translateCategoryName]);

  // Use all categories (no local filtering - use header search for global search)
  const filteredCategories = useMemo(() => {
    return allLevel3Categories;
  }, [allLevel3Categories]);

  // Filter and sort products based on filters (no local search - use header search for global search)
  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    
    // Apply price range filter
    filtered = filtered.filter(product => {
      const price = product.price || product.current_price || product.selling_price || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'price-low':
          const priceA = a.price || a.current_price || a.selling_price || 0;
          const priceB = b.price || b.current_price || b.selling_price || 0;
          return priceA - priceB;
        case 'price-high':
          const priceAHigh = a.price || a.current_price || a.selling_price || 0;
          const priceBHigh = b.price || b.current_price || b.selling_price || 0;
          return priceBHigh - priceAHigh;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [products, sortBy, priceRange]);

  const handleCategorySelect = async (categoryId: string, categoryName?: string) => {
    setSelectedCategory(categoryId);
    
    // Load all products for this category from the product table
    setIsLoadingProducts(true);
    setProducts([]); // Clear previous products
    
    // Get the exact category name from the category object (not translated)
    const categoryObj = allLevel3Categories.find(c => c.categoryId === categoryId || c.id === categoryId);
    const exactCategoryName = categoryObj?.categoryName || categoryName;
    
    console.log(`🔍 Filtering products for category:`, {
      categoryId,
      categoryName,
      exactCategoryName,
      categoryObj
    });
    
    try {
      let categoryProducts: any[] = [];
      
      // Step 1: Get the category ID from categories table by name (e.g., "Wheat & Wheat Products")
      let foundCategoryId = categoryId;
      
      if (exactCategoryName) {
        try {
          // Load all categories to find the one matching the name
          const allCategories = await apiService.getCategories();
          console.log(`🔍 Searching for category with name: "${exactCategoryName}"`);
          console.log(`📋 Total categories loaded: ${allCategories.length}`);
          
          // Try exact match first
          let matchingCategory = allCategories.find((cat: any) => 
            cat.name === exactCategoryName || 
            cat.display_name === exactCategoryName
          );
          
          // If no exact match, try case-insensitive match
          if (!matchingCategory) {
            matchingCategory = allCategories.find((cat: any) => 
              cat.name?.toLowerCase() === exactCategoryName?.toLowerCase() ||
              cat.display_name?.toLowerCase() === exactCategoryName?.toLowerCase()
            );
          }
          
          // If still no match, try partial match
          if (!matchingCategory) {
            matchingCategory = allCategories.find((cat: any) => 
              cat.name?.toLowerCase().includes(exactCategoryName?.toLowerCase()) ||
              exactCategoryName?.toLowerCase().includes(cat.name?.toLowerCase())
            );
          }
          
          if (matchingCategory) {
            foundCategoryId = matchingCategory.id;
            console.log(`✅ Found category ID ${foundCategoryId} for name "${exactCategoryName}"`);
            console.log(`📋 Category details:`, {
              id: matchingCategory.id,
              name: matchingCategory.name,
              display_name: matchingCategory.display_name
            });
          } else {
            console.log(`⚠️ Category "${exactCategoryName}" not found in categories table, using provided categoryId: ${categoryId}`);
          }
        } catch (error) {
          console.error('Error loading categories:', error);
        }
      }
      
      // Step 2: Use the category ID to get products where category_id matches in product table
      // The API endpoint /products/list?subcategory_id={id} should return only products for that category
      if (foundCategoryId) {
        try {
          // Use subcategory_id parameter - the API will filter and return only matching products
          // Do NOT set a limit - let the API return exactly what it finds
          const apiProducts = await apiService.getProducts({ 
            subcategory_id: foundCategoryId
            // No limit parameter - trust the API to return the correct filtered results
          });
          console.log(`📦 API returned ${apiProducts.length} products for category_id (subcategory_id): ${foundCategoryId}`);
          
          // Validate: Filter client-side to ensure only products with matching category_id are shown
          // This is a safety check in case the API returns incorrect results
          categoryProducts = (apiProducts || []).filter((product: any) => {
            // Check multiple possible field names for category_id
            const productCategoryId = product.category_id || 
                                    product.categoryId || 
                                    product.subcategory_id ||
                                    product.subcategoryId;
            
            const matches = productCategoryId && 
                          productCategoryId.toString() === foundCategoryId.toString();
            
            if (!matches && apiProducts.length > 0) {
              console.log(`⚠️ Product "${product.name}" has category_id ${productCategoryId}, expected ${foundCategoryId} - filtering out`);
            }
            
            return matches;
          });
          
          console.log(`✅ After validation: ${categoryProducts.length} products match category_id ${foundCategoryId}`);
          console.log(`📋 Product names:`, categoryProducts.map((p: any) => p.name));
          
          // If no products found, log for debugging
          if (!categoryProducts || categoryProducts.length === 0) {
            console.log(`⚠️ No products found for category_id: ${foundCategoryId}`);
          }
        } catch (error) {
          console.error('Error loading products by category_id:', error);
          // Don't fall back to loading all products - the API should work correctly
        }
      }
      
      // Fallback: Try by category name if category_id approach didn't work
      if ((!categoryProducts || categoryProducts.length === 0) && exactCategoryName) {
        try {
          categoryProducts = await apiService.getProducts({ 
            category: exactCategoryName
            // No limit - let API return what it finds
          });
          console.log(`📦 Loaded ${categoryProducts.length} products using category name: ${exactCategoryName}`);
        } catch (error) {
          console.error('Error loading products by category name:', error);
        }
      }
      
      setProducts(categoryProducts || []);
      console.log(`✅ Total products loaded for category "${exactCategoryName}" (ID: ${foundCategoryId}): ${categoryProducts?.length || 0}`);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleAddToCartClick = (product: any) => {
    setSelectedProductForCart(product);
    setShowShopModal(true);
  };

  // Wishlist functions
  const isInWishlist = (productId: string): boolean => {
    return wishlist.some(item => String(item.id) === String(productId));
  };

  const handleAddToWishlist = async (product: any) => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage for guests
      if (isInWishlist(product.id)) {
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('wishlistUpdated'));
      } else {
        const wishlistItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('wishlistUpdated'));
      }
      return;
    }

    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist via API
        await apiService.removeFromWishlist(user.id, String(product.id));
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
        console.log('✅ Removed from wishlist:', product.name);
      } else {
        // Add to wishlist via API
        const productData = {
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        await apiService.addToWishlist(user.id, String(product.id), productData);
        const wishlistItem = { id: product.id, ...productData };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
        console.log('✅ Added to wishlist:', product.name);
      }
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } catch (error) {
      console.error('Error updating wishlist:', error);
      // Fallback to localStorage on error
      if (isInWishlist(product.id)) {
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
      } else {
        const wishlistItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
      }
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    }
  };

  const handleShopSelect = (shop: Shop, product: Product) => {
    // Add product to cart with selected shop
    try {
      // Validate inputs
      if (!shop || !shop.id) {
        throw new Error('Invalid shop data');
      }
      if (!product || !product.id) {
        throw new Error('Invalid product data');
      }

      console.log('🛒 BrowseAllPage: Adding product to cart:', {
        productId: product.id,
        productName: product.name,
        shopId: shop.id,
        shopName: shop.name
      });

      // Get existing cart from localStorage
      const existingCart = localStorage.getItem('multiShopCart');
      let cart: Array<{ shop: Shop; items: Array<{ product: ShopProduct; quantity: number }> }> = [];
      
      if (existingCart) {
        try {
          const parsed = JSON.parse(existingCart);
          
          // Validate cart structure - handle both nested and flat formats
          if (Array.isArray(parsed) && parsed.length > 0) {
            const firstItem = parsed[0];
            
            // Check if it's nested format: [{shop: {...}, items: [...]}]
            if (firstItem.shop && Array.isArray(firstItem.items)) {
              cart = parsed;
            } 
            // Check if it's flat format: [{product: {...}, shop: {...}, quantity: number}]
            else if (firstItem.product && firstItem.shop && firstItem.quantity !== undefined) {
              console.log('🛒 BrowseAllPage: Converting flat cart format to nested format');
              // Convert flat format to nested format
              const shopMap = new Map<string, { shop: Shop; items: Array<{ product: ShopProduct; quantity: number }> }>();
              
              parsed.forEach((item: any) => {
                if (!item.shop || !item.product) {
                  console.warn('⚠️ BrowseAllPage: Skipping invalid cart item:', item);
                  return;
                }
                
                const shopId = String(item.shop.id);
                if (!shopMap.has(shopId)) {
                  shopMap.set(shopId, { shop: item.shop, items: [] });
                }
                
                const shopCart = shopMap.get(shopId)!;
                const existingItem = shopCart.items.find(i => 
                  i.product && String(i.product.id) === String(item.product.id)
                );
                
                if (existingItem) {
                  existingItem.quantity += item.quantity || 1;
                } else {
                  shopCart.items.push({ product: item.product, quantity: item.quantity || 1 });
                }
              });
              
              cart = Array.from(shopMap.values());
            } else {
              console.warn('⚠️ BrowseAllPage: Unknown cart format, resetting');
              cart = [];
            }
          } else if (!Array.isArray(parsed)) {
            console.warn('⚠️ BrowseAllPage: Cart is not an array, resetting');
            cart = [];
          }
        } catch (e) {
          console.error('❌ BrowseAllPage: Error parsing cart:', e);
          cart = []; // Reset cart if parsing fails
        }
      }

      // Ensure cart is always an array
      if (!Array.isArray(cart)) {
        console.warn('⚠️ BrowseAllPage: Cart is not an array after processing, resetting');
        cart = [];
      }

      // Convert product to ShopProduct format with all required fields
      const productId = String(product.id);
      const productAny = product as any; // Type assertion for dynamic fields
      const shopProduct: ShopProduct = {
        ...productAny,
        id: productId,
        shopId: shop.id,
        shop_id: shop.id,
        name: product.name || 'Unknown Product',
        price: product.price || productAny.current_price || productAny.selling_price || 0,
        category: product.category || productAny.category_name || '',
        category_id: product.category_id || productAny.categoryId || '',
        topCategory: productAny.topCategory || productAny.top_category || '',
        brand: product.brand || '',
        image: product.image || '',
        stock: productAny.stock_quantity || productAny.stock || 0,
        stock_quantity: productAny.stock_quantity || productAny.stock || 0,
        inStock: product.inStock !== undefined ? product.inStock : true,
        rating: productAny.rating || 0,
        reviews: productAny.reviews || productAny.total_reviews || 0,
        discount: productAny.discount || 0,
        createdAt: product.created_at || productAny.createdAt || new Date().toISOString(),
        created_at: product.created_at || productAny.createdAt || new Date().toISOString(),
        updatedAt: product.updated_at || productAny.updatedAt || new Date().toISOString(),
        updated_at: product.updated_at || productAny.updatedAt || new Date().toISOString()
      };

      // Find or create shop entry in cart
      let shopCart = cart.find(c => c && c.shop && c.shop.id === shop.id);
      
      if (!shopCart) {
        shopCart = { shop, items: [] };
        cart.push(shopCart);
      }

      // Ensure shopCart.items is an array
      if (!Array.isArray(shopCart.items)) {
        console.warn('⚠️ BrowseAllPage: shopCart.items is not an array, initializing');
        shopCart.items = [];
      }

      // Check if product already exists in this shop's cart (use String comparison for consistency)
      const existingItem = shopCart.items.find(item => 
        item && item.product && String(item.product.id) === productId
      );
      
      if (existingItem) {
        existingItem.quantity += 1;
        console.log('🛒 BrowseAllPage: Increased quantity for existing item');
      } else {
        shopCart.items.push({ product: shopProduct, quantity: 1 });
        console.log('🛒 BrowseAllPage: Added new item to cart');
      }

      // Save to localStorage with error handling
      try {
        const cartJson = JSON.stringify(cart);
        
        // Check localStorage quota (approximate check)
        if (cartJson.length > 5 * 1024 * 1024) { // 5MB limit
          throw new Error('Cart is too large. Please remove some items.');
        }
        
        localStorage.setItem('multiShopCart', cartJson);
        console.log('✅ BrowseAllPage: Cart saved to localStorage, cart structure:', cart.length, 'shops');
        
        // Calculate total count
        const totalCount = cart.reduce((sum, shopCart) => 
          sum + (shopCart.items || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0), 0
        );
        console.log('🛒 BrowseAllPage: Calculated total count:', totalCount);
        
        // Dispatch cart update event immediately, then again with delay as fallback
        const event = new CustomEvent('cartUpdated', { 
          detail: { 
            cartCount: totalCount, 
            source: 'BrowseAllPage',
            shopId: shop.id,
            productId: productId
          } 
        });
        window.dispatchEvent(event);
        console.log('🛒 BrowseAllPage: cartUpdated event dispatched immediately, count:', totalCount);
        
        // Also dispatch with small delay as fallback
        setTimeout(() => {
          window.dispatchEvent(event);
          console.log('🛒 BrowseAllPage: cartUpdated event dispatched (fallback)');
        }, 10);

        console.log('✅ BrowseAllPage: Product added to cart successfully:', { 
          product: product.name, 
          shop: shop.name,
          quantity: existingItem ? existingItem.quantity : 1
        });
        
        // Close modal and show success message
        setShowShopModal(false);
        setSelectedProductForCart(null);
        
        // Show success message (optional - can be replaced with toast notification)
        alert(`✅ Added ${product.name} to cart from ${shop.name}`);
      } catch (error: any) {
        console.error('❌ BrowseAllPage: Error saving cart:', error);
        const errorMessage = error?.message || 'Unknown error occurred';
        throw new Error(`Failed to save cart: ${errorMessage}`);
      }
    } catch (error: any) {
      console.error('❌ BrowseAllPage: Error adding to cart:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to add product to cart: ${errorMessage}. Please try again.`);
    }
  };

  return (
    <div className="browse-all-page">
      {/* Header - Using CustomerHeader component for consistency */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={false}
        showLoyaltyButton
        cartCount={cartCount}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Navigation Buttons */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${location.pathname === '/app' || location.pathname === '/' || location.pathname === '/customer' || location.pathname === '/customer/home' ? 'active' : ''}`} 
              onClick={() => navigate('/app')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/explore-shops' || location.pathname === '/shops' ? 'active' : ''}`} 
              onClick={() => navigate('/explore-shops')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/browse-all' || location.pathname === '/browse' ? 'active' : ''}`} 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/services' || location.pathname.startsWith('/services/') ? 'active' : ''}`} 
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="browse-all-content">
        <div className="body-container">
          {!selectedCategory ? (
            /* Show All Level 3 Categories */
            <div>
              {/* Page Title */}
              <div className="browse-all-header">
                <div className="browse-all-header-content">
                  <div className="browse-all-header-text">
                    <h2>🛍️ {t.browseAllProducts || 'Browse All Products'}</h2>
                    <p>Discover products from all categories</p>
                  </div>
                </div>
              </div>

            {isLoadingCategories ? (
              <div className="loading-state">
                <p>Loading categories...</p>
              </div>
            ) : allLevel3Categories.length > 0 ? (
              <div className="category-section">
                <h2 className="section-title" style={{ marginBottom: '24px', fontSize: '28px', fontWeight: 700 }}>
                  {t.browseAll || 'Browse All Categories'}
                </h2>
                
                {/* Removed page-level search - use header search for global search */}
                
                <div className="category-grid">
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((category: any) => (
                    <button
                      key={category.id}
                      className="category-button"
                      onClick={() => handleCategorySelect(category.categoryId || category.id, category.categoryName || category.name)}
                    >
                      {category.image ? (
                        <div className="category-image-wrapper">
                          <img 
                            src={getValidImageUrl(category.image)} 
                            alt={category.name}
                            className="category-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent && !parent.querySelector('.category-icon-fallback')) {
                                const fallback = document.createElement('span');
                                fallback.className = 'category-icon-fallback';
                                fallback.textContent = category.icon;
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span className="category-icon">{category.icon}</span>
                      )}
                      <span className="category-name">{category.name}</span>
                    </button>
                    ))
                  ) : (
                    <div className="no-results">
                      <p>No categories available</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-results">
                <p>No level 3 categories found.</p>
              </div>
            )}
          </div>
        ) : (
          /* Show Products for Selected Category */
          <div>
            {/* Breadcrumbs */}
            <nav className="breadcrumbs" aria-label="Breadcrumb">
              <button 
                className="breadcrumb-link"
                onClick={() => {
                  setSelectedCategory(null);
                  setProducts([]);
                  setSortBy('name');
                  setPriceRange({ min: 0, max: 10000 });
                }}
              >
                Home
              </button>
              <span className="breadcrumb-separator">›</span>
              <button 
                className="breadcrumb-link"
                onClick={() => {
                  setSelectedCategory(null);
                  setProducts([]);
                }}
              >
                Categories
              </button>
              {selectedCategory && (
                <>
                  <span className="breadcrumb-separator">›</span>
                  <span className="breadcrumb-current">
                    {(() => {
                      const category = allLevel3Categories.find(c => c.categoryId === selectedCategory || c.id === selectedCategory);
                      return category ? category.name : 'Category';
                    })()}
                  </span>
                </>
              )}
            </nav>

            {/* Category Title */}
            <div className="category-header">
              <h2 className="section-header-title" style={{ margin: '32px 0 24px 0' }}>
                {(() => {
                  const category = allLevel3Categories.find(c => c.categoryId === selectedCategory || c.id === selectedCategory);
                  return category ? category.name : 'Category';
                })()}
              </h2>
              {isLoadingProducts && (
                <p className="loading-text">Loading products...</p>
              )}
            </div>

            {/* Filter Bar for Products (removed page-level search - use header search for global search) */}
            <div className="browse-search-filter-bar">
              <div className="browse-filter-actions">
                <button 
                  className="browse-filter-toggle-btn"
                  onClick={() => setShowFilters(!showFilters)}
                  aria-label="Toggle filters"
                >
                  <span>⚙️</span>
                  <span>Filters</span>
                  {showFilters && <span className="filter-indicator">●</span>}
                </button>
                
                <div className="browse-sort-container">
                  <label htmlFor="sort-select" className="browse-sort-label">Sort:</label>
                  <select
                    id="sort-select"
                    className="browse-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'price-low' | 'price-high')}
                    aria-label="Sort products"
                  >
                    <option value="name">Name (A-Z)</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>
              </div>
              
              {/* Filter Panel */}
              {showFilters && (
                <div className="browse-filter-panel">
                  <div className="filter-panel-header">
                    <h3>Filter Products</h3>
                    <button 
                      className="filter-close-btn"
                      onClick={() => setShowFilters(false)}
                      aria-label="Close filters"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="filter-panel-content">
                    <div className="filter-group">
                      <label htmlFor="price-min" className="filter-label">Min Price (₹)</label>
                      <input
                        id="price-min"
                        type="number"
                        className="filter-input"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange({ ...priceRange, min: Number(e.target.value) || 0 })}
                        min="0"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="price-max" className="filter-label">Max Price (₹)</label>
                      <input
                        id="price-max"
                        type="number"
                        className="filter-input"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange({ ...priceRange, max: Number(e.target.value) || 10000 })}
                        min="0"
                      />
                    </div>
                    <button 
                      className="filter-reset-btn"
                      onClick={() => {
                        setPriceRange({ min: 0, max: 10000 });
                      }}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              )}
              
              {(priceRange.min > 0 || priceRange.max < 10000) && (
                <div className="browse-search-results-count">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
                </div>
              )}
            </div>

            {/* Products Section */}
            {isLoadingProducts ? (
              <div className="loading-state">
                <p>Loading products...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="products-section">
                <div className="products-grid">
                  {filteredProducts.map((product: any) => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        {product.image && !product.image.startsWith('📦') && !product.image.startsWith('🛍️') ? (
                          <img 
                            src={getValidImageUrl(product.image)} 
                            alt={product.name}
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="product-emoji">📦</span>
                        )}
                        <button
                          className={`wishlist-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToWishlist(product);
                          }}
                          aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                          title={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                        >
                          {isInWishlist(product.id) ? '❤️' : '🤍'}
                        </button>
                      </div>
                      <div className="product-info">
                        <div className="product-name">{product.name}</div>
                        {/* Description hidden in grid view for better UX - show on product detail page */}
                        {product.brand && (
                          <div className="product-brand">Brand: {product.brand}</div>
                        )}
                        <div className="product-price">
                          <span className="current-price">
                            ₹{product.price || product.current_price || product.selling_price || '0.00'}
                          </span>
                          {product.original_price && product.original_price > (product.price || product.current_price || 0) && (
                            <span className="original-price">
                              ₹{product.original_price}
                            </span>
                          )}
                          {product.discount && product.discount > 0 && (
                            <span className="discount-badge">{product.discount}% OFF</span>
                          )}
                        </div>
                        <button 
                          className="add-btn"
                          onClick={() => handleAddToCartClick(product)}
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-results">
                <p>
                  {priceRange.min > 0 || priceRange.max < 10000
                    ? `No products found matching your filters.`
                    : 'No products found for this category.'}
                </p>
                {(priceRange.min > 0 || priceRange.max < 10000) && (
                  <button
                    className="clear-filters-btn"
                    onClick={() => {
                      setPriceRange({ min: 0, max: 10000 });
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Shop Selection Modal - Contextual Only
          This modal appears ONLY when user clicks "Add to Cart" on a product.
          Shops are NOT displayed as primary content on this page.
          For shop discovery, users should navigate to "Explore Shops" page.
      */}
      <ShopSelectionModal
        isOpen={showShopModal}
        product={selectedProductForCart}
        onClose={() => {
          setShowShopModal(false);
          setSelectedProductForCart(null);
        }}
        onSelectShop={handleShopSelect}
      />
    </div>
  );
};

export default BrowseAllPage;
